import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { TrendingUp, Trophy, Users, ArrowRight, Flame } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Skeleton } from '../ui/Skeleton'
import LazyImage from '../ui/LazyImage'
import leaderboardService from '../../services/leaderboardService'

const TrendingWidget = ({ className = '' }) => {
  const navigate = useNavigate()

  const {
    data: trendingData,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['trending-widget'],
    queryFn: () => leaderboardService.getTrendingProjects(3),
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    retry: 1,
    refetchOnWindowFocus: false
  })

  const handleViewAll = () => {
    navigate('/leaderboards?tab=trending')
  }

  const handleViewProject = (slug) => {
    navigate(`/projects/${slug}`)
  }

  const LoadingSkeleton = () => (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="flex items-center gap-3 animate-pulse">
          <Skeleton className="w-12 h-12 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  )

  const ErrorState = () => (
    <div className="text-center py-6">
      <TrendingUp className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
      <p className="text-sm text-neutral-600">Unable to load trending projects</p>
    </div>
  )

  const EmptyState = () => (
    <div className="text-center py-6">
      <Flame className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
      <p className="text-sm text-neutral-600">No trending projects at the moment</p>
    </div>
  )

  const projects = trendingData?.leaderboard || []

  return (
    <Card className={`border-0 shadow-soft hover:shadow-medium transition-all duration-300 bg-white/90 backdrop-blur-sm ${className}`}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
              <Flame className="w-4 h-4 text-white" />
            </div>
            <CardTitle className="text-lg font-semibold text-neutral-900">
              Trending Projects
            </CardTitle>
          </div>
          <Badge variant="secondary" className="bg-orange-100 text-orange-800">
            <TrendingUp className="w-3 h-3 mr-1" />
            Hot
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <LoadingSkeleton />
        ) : isError ? (
          <ErrorState />
        ) : projects.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-4">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-neutral-50/80 transition-all duration-200 cursor-pointer group"
                onClick={() => handleViewProject(project.slug)}
              >
                {/* Rank */}
                <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-white">
                    {index + 1}
                  </span>
                </div>

                {/* Project Image */}
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 shadow-sm">
                  {project.cover_image ? (
                    <LazyImage
                      src={project.cover_image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      fallback={
                        <div className="w-full h-full flex items-center justify-center bg-primary-teal/10">
                          <TrendingUp className="w-4 h-4 text-primary-teal" />
                        </div>
                      }
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary-teal/10">
                      <TrendingUp className="w-4 h-4 text-primary-teal" />
                    </div>
                  )}
                </div>

                {/* Project Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-neutral-900 mb-1 line-clamp-1 group-hover:text-primary-teal transition-colors">
                    {project.title}
                  </h4>
                  <div className="flex items-center gap-2 text-xs text-neutral-600">
                    <div className="flex items-center gap-1">
                      <Trophy className="w-3 h-3" />
                      <span>{project.funding_display}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      <span>{project.supporter_count}</span>
                    </div>
                  </div>
                </div>

                {/* Trending Indicator */}
                <div className="flex items-center gap-1 text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs font-medium">Hot</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* View All Button */}
        {projects.length > 0 && (
          <div className="pt-4 mt-4 border-t border-neutral-100">
            <Button
              onClick={handleViewAll}
              variant="ghost"
              className="w-full justify-center text-primary-teal hover:text-primary-teal hover:bg-primary-teal/5"
            >
              View All Leaderboards
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default TrendingWidget